import { useEffect } from 'react'
import { Alert, DatePicker, Form, Input, Modal } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { tenantToday } from '@/shared/lib/dayRange'
import { REASON_MAX, type Person } from './hrApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

const DATE_FORMAT = 'YYYY-MM-DD'

interface PersonTerminateModalProps {
  open: boolean
  person: Person
  submitting: boolean
  /**
   * ⚠️ Amalning O'ZI bu yerda bajarilMAYDI — sabab `OperationResult`
   * uch holatli va uni ko'rsatish sahifaning ishi
   * (`useOperationRunner` + `OperationResultAlert`). Modal natijani
   * o'zi chizsa, `PARTIAL` holati modal yopilgandan keyin ekrandan
   * yo'qolardi — aynan o'qilishi kerak bo'lgan xabar.
   */
  onSubmit: (terminatedAt: string, reason: string) => void
  onClose: () => void
}

interface FormValues {
  terminatedAt: Dayjs
  reason: string
}

/**
 * Ishdan bo'shatish — `DELETE` O'RNIDA.
 *
 * <h2>⚠️ NEGA ALOHIDA OYNA, HOLAT TANLAGICHI EMAS</h2>
 * `POST /state` `TERMINATED` ni ATAYLAB rad etadi
 * (`PersonStateChange` javadoc'i): ishdan bo'shatish oddiy maydon
 * tahriri emas, u IKKI ishni bitta tranzaksiyada bajaradi — ochiq
 * tayinlash davrini yopadi VA holatni qo'yadi. Ikkalasi ham sana talab
 * qiladi.
 *
 * <h2>⚠️ XODIM O'CHIRILMAYDI</h2>
 * Uning tayinlash tarixi maosh hisobotining manbai (§20.9-E). Yozuvni
 * o'chirish "o'tgan yilgi maosh xarajati" savolini javobsiz qoldirardi.
 * Oynada bu ochiq yoziladi, aks holda operator uni "xato kiritdim,
 * tozalayman" deb bosardi.
 */
export function PersonTerminateModal({
  open,
  person,
  submitting,
  onSubmit,
  onClose,
}: PersonTerminateModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()

  useEffect(() => {
    if (!open) return
    // ⚠️ TENANT kalendaridagi bugun (`dayRange.ts`)
    form.setFieldsValue({ terminatedAt: tenantToday(), reason: '' })
  }, [open, form])

  return (
    <Modal
      open={open}
      title={t('hr.person.terminateTitle', { name: person.fullName })}
      onCancel={onClose}
      onOk={() =>
        submitOnValid(form, (values) => onSubmit(values.terminatedAt.format(DATE_FORMAT), values.reason.trim()))
      }
      confirmLoading={submitting}
      okText={t('hr.person.terminateAction')}
      okButtonProps={{ danger: true }}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('hr.person.terminateNote')}
      />

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={submitting}>
        <Form.Item
          name="terminatedAt"
          label={t('hr.person.terminatedAt')}
          extra={t('hr.person.terminatedAtHint')}
          rules={[
            { required: true, message: t('hr.person.validation.terminatedAtRequired') },
            {
              validator: (_rule, value: Dayjs | null) => {
                // ⚠️ Baza ham to'sadi (`person_terminated_after_hire`).
                // Bu yerdagi tekshiruv operatorni server xatosigacha
                // bormasdan to'xtatadi.
                if (!value) return Promise.resolve()
                return value.isBefore(dayjs(person.hiredAt, DATE_FORMAT), 'day')
                  ? Promise.reject(new Error(t('hr.person.validation.terminatedBeforeHire')))
                  : Promise.resolve()
              },
            },
          ]}
        >
          <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="reason"
          label={t('hr.person.terminateReason')}
          extra={t('hr.person.terminateReasonHint')}
          rules={[
            { required: true, message: t('hr.person.validation.reasonRequired') },
            { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
          ]}
        >
          <Input.TextArea rows={3} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  )
}
